import { Alert, Button } from "@opowiedzo/ui";
import {
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useInvalidate,
  useOne,
} from "@refinedev/core";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { Badge } from "@/components/Badge";
import {
  BackLink,
  Card,
  DefinitionList,
  DetailGrid,
  ErrorBox,
  Loading,
  PageHeader,
} from "@/components/Page";
import {
  answeredViaLabel,
  formatDateTime,
  recordingStatus,
  storyStatus,
  storyVersionLabel,
  transcriptStatus,
} from "@/lib/format";
import type { AdminIdentity } from "@/providers/authProvider";
import type { StoryRow } from "./list";

export interface RecordingRow extends Record<string, unknown> {
  id: string;
  memoir_id: string;
  memoir_title: string | null;
  story_id: string | null;
  prompt_delivery_id: string | null;
  status: string;
  failure_reason: string | null;
  transcript_status: string;
  transcript_error: string | null;
  duration_s: number | null;
  created_at: string;
  updated_at: string;
}

interface StoryDetail extends StoryRow {
  prompt_delivery_id: string | null;
  proxy_member_name: string | null;
  recordings: RecordingRow[];
}

interface StoryContent extends Record<string, unknown> {
  title: string | null;
  versions: { kind: string; body_md: string; created_at: string }[];
}

export function StoryShowPage() {
  const { id = "" } = useParams();
  const { query } = useOne<StoryDetail>({ resource: "stories", id });
  const { data: identity } = useGetIdentity<AdminIdentity | null>();
  const story = query.data?.data;

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!story) return null;

  const status = storyStatus(story.status);

  return (
    <>
      <BackLink to="/historie">Historie</BackLink>
      <PageHeader
        title="Historia"
        description={story.memoir_title ?? undefined}
        actions={<Badge tone={status.tone}>{status.label}</Badge>}
      />

      <DetailGrid
        main={
          <>
            <Card title="Pytanie">
              <p className="text-lg text-ink">{story.question ?? "-"}</p>
              <p className="mt-2 text-sm text-ink-soft">
                {story.category_name ?? "bez kategorii"}
                {story.sensitive && " - temat delikatny"}
              </p>
            </Card>

            <ContentPanel
              storyId={story.id}
              contentAccess={identity?.content_access ?? false}
              versionsCount={story.versions_count}
            />

            <RecordingsPanel
              storyId={story.id}
              recordings={story.recordings}
              allowed={(identity?.permissions ?? []).includes("stories.act")}
            />
          </>
        }
        side={
          <Card title="Metadane">
            <DefinitionList
              rows={[
                ["Opublikowana", formatDateTime(story.published_at)],
                ["Powstała", formatDateTime(story.created_at)],
                ["Kanał odpowiedzi", answeredViaLabel(story.answered_via)],
                ["Zapisał", story.proxy_member_name ?? "narrator"],
                ["Temat delikatny", story.sensitive ? "tak" : "nie"],
                ["Ukryta w książce", story.hidden_in_book ? "tak" : "nie"],
                ["Zdjęcia", String(story.photos_count)],
                ["Wersje", String(story.versions_count)],
                [
                  "Opowieść",
                  <Link
                    key="memoir"
                    to={`/opowiesci/${story.memoir_id}`}
                    className="text-accent underline underline-offset-4 hover:text-accent-dark"
                  >
                    {story.memoir_title ?? "otwórz"}
                  </Link>,
                ],
                [
                  "Wysyłka pytania",
                  story.prompt_delivery_id ? (
                    <Link
                      to={`/wysylki-pytan/${story.prompt_delivery_id}`}
                      className="text-accent underline underline-offset-4 hover:text-accent-dark"
                    >
                      otwórz
                    </Link>
                  ) : (
                    "-"
                  ),
                ],
              ]}
            />
          </Card>
        }
      />
    </>
  );
}

function ContentPanel({
  storyId,
  contentAccess,
  versionsCount,
}: {
  storyId: string;
  contentAccess: boolean;
  versionsCount: number;
}) {
  // Treść pobieramy dopiero po kliknięciu. Każde takie żądanie zostawia wpis
  // w audycie, więc nie wolno go wysyłać "na wszelki wypadek" przy wejściu.
  const [pokaz, setPokaz] = useState(false);
  const { query } = useCustom<StoryContent>({
    url: `/stories/${storyId}/content`,
    method: "get",
    queryOptions: { enabled: pokaz },
  });

  return (
    <Card title="Treść">
      {!contentAccess ? (
        <p className="text-base text-ink-soft">
          Treść jest zamknięta. Wgląd wymaga osobnego uprawnienia, które superadmin włącza na czas
          konkretnego zgłoszenia.
        </p>
      ) : versionsCount === 0 ? (
        <p className="text-base text-ink-soft">Ta historia nie ma jeszcze żadnej wersji tekstu.</p>
      ) : !pokaz ? (
        <Button onClick={() => setPokaz(true)}>Pokaż treść (zostanie zapisane w audycie)</Button>
      ) : query.isLoading ? (
        <Loading />
      ) : query.error ? (
        <ErrorBox error={query.error} />
      ) : (
        <div className="flex flex-col gap-3.5">
          <p className="font-display text-lg font-semibold text-ink">
            {query.data?.data.title ?? "-"}
          </p>
          {query.data?.data.versions.map((version) => (
            <article key={`${version.kind}-${version.created_at}`}>
              <p className="text-sm text-ink-soft">{storyVersionLabel(version.kind)}</p>
              <p className="text-base leading-relaxed whitespace-pre-line text-ink">
                {version.body_md}
              </p>
            </article>
          ))}
        </div>
      )}
    </Card>
  );
}

export function RecordingsPanel({
  storyId,
  recordings,
  allowed,
}: {
  storyId?: string;
  recordings: RecordingRow[];
  allowed: boolean;
}) {
  const invalidate = useInvalidate();
  const { mutate, mutation } = useCustomMutation();

  if (recordings.length === 0) {
    return (
      <Card title="Nagrania">
        <p className="text-base text-ink-soft">Do tej historii nie ma nagrania.</p>
      </Card>
    );
  }

  const ponow = (recordingId: string) =>
    mutate(
      {
        url: `/recordings/${recordingId}/retry-transcription`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          invalidate({ resource: "recordings", invalidates: ["list"] });
          if (storyId) {
            invalidate({ resource: "stories", invalidates: ["detail"], id: storyId });
          }
        },
      },
    );

  return (
    <Card title="Nagrania">
      {mutation.error && <Alert tone="error">{mutation.error.message}</Alert>}
      <ul className="flex flex-col gap-3.5">
        {recordings.map((recording) => {
          const plik = recordingStatus(recording.status);
          const tekst = transcriptStatus(recording.transcript_status);
          return (
            <li key={recording.id} className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="flex flex-wrap items-center gap-2">
                  <Badge tone={plik.tone}>{plik.label}</Badge>
                  <Badge tone={tekst.tone}>{tekst.label}</Badge>
                  <span className="tabular text-sm text-ink-soft">
                    {recording.duration_s ? `${recording.duration_s} s` : "długość nieznana"}
                  </span>
                </p>
                <p className="mt-1 text-sm text-ink-soft">
                  {formatDateTime(recording.created_at)}
                  {recording.transcript_error && ` - ${recording.transcript_error}`}
                  {recording.failure_reason && ` - ${recording.failure_reason}`}
                </p>
              </div>
              {allowed && recording.status === "ready" && recording.transcript_status !== "done" && (
                <Button disabled={mutation.isPending} onClick={() => ponow(recording.id)}>
                  Ponów transkrypcję
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
